import type { UpcomingPolicyStep } from "@unbox/api";
import { ChevronDown, ChevronUp } from "@untitledui/icons";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { splitSequenceSteps, stepLabel } from "@/pages/sequences/sequence-steps-preview";
import type { ActivityItem } from "./ActivityFeed";
import { ActivityRow } from "./ActivityFeed";

type UpcomingPolicyActionsProps = {
  steps: UpcomingPolicyStep[];
};

const upcomingStepLabel = (step: Pick<UpcomingPolicyStep, "type">) =>
  stepLabel({ type: step.type, messageTemplateId: null, customMessage: null });

export const UpcomingPolicyActions = ({ steps }: UpcomingPolicyActionsProps) => {
  const [open, setOpen] = useState(false);
  const { trunk, branchStep, takenArm, notTakenArm, armLabels } = splitSequenceSteps(steps);

  // Once the branch has already resolved, the branch step itself is gone (it already
  // executed, so the API excludes it) and its steps land in whichever arm was taken —
  // not in trunk. Render those as ordinary upcoming steps instead of the speculative
  // "if/else" treatment below, which only makes sense while the outcome is still unknown.
  const resolvedSteps = branchStep ? trunk : [...trunk, ...takenArm, ...notTakenArm].sort((a, b) => a.step - b.step);

  const rows: ActivityItem[] = [];

  resolvedSteps.forEach((step, index) => {
    if (step.delayDays > 0 && index > 0) {
      rows.push({ id: `wait-${step.step}`, actor: "unbox", title: "", waitDays: step.delayDays });
    }
    rows.push({
      id: `step-${step.step}`,
      actor: "unbox",
      title: upcomingStepLabel(step),
      timestamp: step.dueAt ?? undefined,
      message: step.messagePreview ?? undefined,
    });
  });

  if (branchStep) {
    if (branchStep.delayDays > 0) {
      rows.push({ id: `wait-${branchStep.step}`, actor: "unbox", title: "", waitDays: branchStep.delayDays });
    }
    rows.push({
      id: "branch-taken",
      actor: "unbox",
      title: "",
      plainText: `${armLabels?.taken ?? "If true"} → ${takenArm[0] ? upcomingStepLabel(takenArm[0]) : "No further action"}`,
    });
    rows.push({
      id: "branch-not-taken",
      actor: "unbox",
      title: "",
      plainText: `${armLabels?.notTaken ?? "If false"} → ${notTakenArm[0] ? upcomingStepLabel(notTakenArm[0]) : "No further action"}`,
    });
  }

  if (rows.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border border-dashed border-primary rounded-md p-4 flex flex-col gap-5 mb-3 bg-white">
        <CollapsibleTrigger className="flex w-full items-center gap-4">
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-secondary m-0">Upcoming policy actions</p>
            <p className="text-xs text-quaternary m-0">What will auto happen next based on creators response</p>
          </div>
          {open ? <ChevronUp className="w-5 h-5 text-secondary" /> : <ChevronDown className="w-5 h-5 text-secondary" />}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col">
            {rows.map((row, i) => (
              <ActivityRow key={row.id} item={row} showConnector={i < rows.length - 1} />
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
